import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  Dot,
  DotRow,
  ProductEmblaContainer,
  ProductEmblaSlide,
  ProductEmblaViewport,
  ProductImage,
  ProductImageArea,
  ProductImagePlaceholder
} from "./styles";
import ProductPlaceholder from "../../../assets/images/shop/product-placeholder.svg";
import { getProductImagesWithFallback, resolveShopImageSrc } from "./shopImageUtils";

interface ProductImageCarouselProps {
  productSlug: string;
  productName: string;
  imageUrls: string[];
}

const ProductImageCarousel = ({ productSlug, productName, imageUrls }: ProductImageCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());
  const images = getProductImagesWithFallback(productSlug, imageUrls);

  const onInit = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onInit();
    onSelect();
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return (
    <>
      <ProductImageArea>
        <ProductEmblaViewport ref={emblaRef}>
          <ProductEmblaContainer>
            {images.map((imageSrc, index) => (
              <ProductEmblaSlide key={`${productName}-img-${index}`}>
                {resolveShopImageSrc(imageSrc) && !brokenImages.has(index) ? (
                  <ProductImage
                    src={resolveShopImageSrc(imageSrc)}
                    alt={productName}
                    onError={() =>
                      setBrokenImages((prev) => {
                        const next = new Set(prev);
                        next.add(index);
                        return next;
                      })
                    }
                  />
                ) : (
                  <ProductImagePlaceholder>
                    <img src={ProductPlaceholder} alt="Produkt Platzhalter" />
                  </ProductImagePlaceholder>
                )}
              </ProductEmblaSlide>
            ))}
          </ProductEmblaContainer>
        </ProductEmblaViewport>
      </ProductImageArea>

      <DotRow>
        {scrollSnaps.map((_, index) => (
          <Dot
            key={`${productName}-dot-${index}`}
            type="button"
            aria-label={`Bild ${index + 1} anzeigen`}
            $active={selectedIndex === index}
            onClick={() => emblaApi?.scrollTo(index)}
          />
        ))}
      </DotRow>
    </>
  );
};

export default ProductImageCarousel;
